import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { errMsg, homeFor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, LifeBuoy, Loader2, ShieldCheck, Ticket, Zap } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forcarTroca, setForcarTroca] = useState(false);
  const [loggedUser, setLoggedUser] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.must_change_password && password === "123456") {
        setLoggedUser(user);
        setForcarTroca(true);
        return;
      }
      navigate(homeFor(user));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (forcarTroca) {
    return <ForcePasswordChange onDone={() => navigate(homeFor(loggedUser))} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8" data-testid="login-page">
      <div className="w-full max-w-5xl bg-zinc-900 border border-zinc-800 overflow-hidden shadow-2xl flex min-h-[560px]" style={{ borderRadius: 24 }} data-testid="login-card">
        <div className="hidden lg:flex flex-col justify-between w-[46%] p-12 border-r border-[#283448] bg-black/20">
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-6 w-6 text-indigo-400" />
          <span className="font-semibold text-lg text-zinc-100 tracking-tight">Help Desk</span>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-indigo-400 mb-4">SaaS Multi-Tenant de Suporte</p>
          <h1 className="text-2xl md:text-3xl tracking-tight font-semibold text-zinc-100 leading-snug mb-6">
            Gestão de chamados para empresas que levam suporte a sério.
          </h1>
          <div className="space-y-4">
            {[
              { icon: Ticket, text: "Chamados com timeline, prioridades e anexos" },
              { icon: ShieldCheck, text: "Isolamento total de dados por organização" },
              { icon: Zap, text: "API REST com chaves por tenant para integrações" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm text-zinc-400">
                <div className="h-8 w-8 rounded-md bg-zinc-800 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-indigo-400" />
                </div>
                {text}
              </div>
            ))}
          </div>
        </div>
          <p className="text-xs text-zinc-500">Help Desk © 2026 — Interaccess Sistemas</p>
        </div>

        <div className="flex-1 flex items-center justify-center p-8 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <LifeBuoy className="h-6 w-6 text-indigo-400" />
            <span className="font-semibold text-lg text-zinc-100">Help Desk</span>
          </div>
          <h2 className="text-xl tracking-tight font-medium text-zinc-100 mb-1">Acessar plataforma</h2>
          <p className="text-sm text-zinc-400 mb-8">Entre com as credenciais da sua organização.</p>

          {error && (
            <div data-testid="login-error" className="text-rose-400 bg-rose-400/10 text-sm rounded-md px-3 py-2 mb-4">{error}</div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs text-zinc-400">E-mail</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com.br"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs text-zinc-400">Senha</Label>
              <div className="relative mt-1.5">
                <Input id="password" data-testid="login-password-input" type={showPassword ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500 pr-10" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors duration-200">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
            <div className="text-center">
              <Link to="/esqueci-senha" data-testid="forgot-password-link"
                className="text-xs text-zinc-400 hover:text-indigo-400 transition-colors duration-200">
                Esqueci minha senha
              </Link>
            </div>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
}
